import { ICustomProductListItem } from '@dtos/MembersConfig'
import { isEmptyPageError } from '@utils/apolloErrors'

// Tamanho da página da lista customizada. Vale para a primeira página (variável
// `$customProductListLimit` do `getMembersConfig`) e para o `loadMore`
// (`getProductsCustomList`): os dois PRECISAM concordar, porque o offset do
// `loadMore` é derivado de quantos itens já estão na tela.
export const CUSTOM_PRODUCT_LIST_PAGE_SIZE = 10

interface INextStateParams {
  current: ICustomProductListItem[]
  incoming?: ICustomProductListItem[] | null
  error?: unknown
}

interface INextState {
  items: ICustomProductListItem[]
  reachedEnd: boolean
}

/**
 * Decide o próximo estado da lista customizada depois de uma página.
 *
 * O core responde `Empty page!` quando o offset passa do fim da lista — e o
 * `useLazyQuery` resolve ESSA chamada com `error` preenchido E `data` ainda
 * apontando para a página ANTERIOR (o último resultado bom do hook). Concatenar
 * olhando só o `data` duplicava a última página inteira: uma trilha de 11 itens
 * mostrava o 11º card duas vezes, e uma de 15 repetia os 5 últimos.
 *
 * Daí as regras:
 * - erro NUNCA concatena, e só `Empty page!` encerra a lista (erro transitório
 *   mantém o gatilho armado para a próxima tentativa);
 * - `data` ausente (query superada por outra, unmount no meio do voo,
 *   `resetStore`) não é fim de lista: o core sempre responde `Empty page!`
 *   quando o offset passa do fim, nunca lista vazia. Encerrar aqui travaria a
 *   lista em 10 cards até o aluno recarregar;
 * - página incompleta encerra a lista (evita até pedir o offset além do fim);
 * - append deduplicado por `id`, porque dois gatilhos podem pedir o mesmo
 *   offset;
 * - append que não cresceu TAMBÉM encerra: o offset da próxima página sai do
 *   tamanho da lista, então uma janela repetida (dois gatilhos no mesmo offset,
 *   ou `order` empatado — o core pagina a relação embedded sem desempate)
 *   deixaria o offset parado pedindo a mesma página para sempre.
 * - quando nada é acrescentado, devolve o MESMO array, para o `setState` do
 *   chamador não re-renderizar a lista inteira à toa.
 */
export const nextCustomProductListState = ({
  current,
  incoming,
  error,
}: INextStateParams): INextState => {
  if (error) {
    return { items: current, reachedEnd: isEmptyPageError(error) }
  }

  if (!incoming) {
    return { items: current, reachedEnd: false }
  }

  if (incoming.length === 0) {
    return { items: current, reachedEnd: true }
  }

  const alreadyListed = new Set(current.map(({ id }) => id))
  const fresh = incoming.filter(({ id }) => !alreadyListed.has(id))

  return {
    items: fresh.length ? [...current, ...fresh] : current,
    reachedEnd:
      incoming.length < CUSTOM_PRODUCT_LIST_PAGE_SIZE || fresh.length === 0,
  }
}
